import { useEffect, useId, useRef, useState } from "react";
import type { JSX } from "react";
import type { AttributionRequestBody, Project } from "../../api/schemas";
import { orderProjects } from "../../domain/projects";
import type { ProjectChoices } from "../../domain/projects";
import { useOnlineStatus } from "../../offline/useOnlineStatus";
import { useI18n } from "../../shared/i18n/I18nContext";
import { mutateThen } from "../../shared/query/mutateThen";
import { Alert } from "../../shared/ui/Alert";
import { cx } from "../../shared/ui/classNames";
import { ErrorMessage } from "../../shared/ui/ErrorMessage";
import styles from "../../shared/ui/ui.module.css";
import { maxRequestReasonLength, validateReason } from "./model/pastDays";
import type { ReasonProblem } from "./model/pastDays";
import { useCreateAttributionRequest } from "./useAttributionRequests";

/** Choice of the requested project; `null` in {@link AttributionRequestFormProps} fixes it. */
export interface RequestProjectChoice {
  /** Selectable projects. */
  readonly projects: readonly Project[];
  /** Favorites and recently used projects. */
  readonly choices: ProjectChoices;
}

/** Properties of {@link AttributionRequestForm}. */
export interface AttributionRequestFormProps {
  /** The time the request is about, for example "22.09.2026, 08:00–10:00". */
  readonly subject: string;
  /** Project selection, or `null` when the project is fixed (a rejected attribution). */
  readonly projectChoice: RequestProjectChoice | null;
  /** Preset (or fixed) project; `null` removes the attribution. */
  readonly initialProjectId: string | null;
  /** Label of a fixed project. */
  readonly fixedProjectLabel: string;
  /** Builds the requested attribution for the chosen project. */
  readonly buildAttribution: (projectId: string | null) => AttributionRequestBody;
  /** Called after the request was stored. */
  readonly onDone: () => void;
  /** Closes the form without a request. */
  readonly onCancel: () => void;
}

/**
 * "Änderung beantragen" (F-07, spec 6.5.6 `PINV-P05`): asks the project responsibility to accept an
 * attribution in a locked project period. The reason is mandatory; requests need a connection, so
 * the form explains that and disables sending while offline. The first field gets the focus when the
 * form opens (UX-110), errors are tied to their field (UX-111).
 *
 * @param props - Component properties.
 * @returns The form.
 */
export function AttributionRequestForm(props: AttributionRequestFormProps): JSX.Element {
  const { t } = useI18n();
  const online = useOnlineStatus();
  const create = useCreateAttributionRequest();
  const idPrefix = useId();
  const [projectId, setProjectId] = useState<string | null>(props.initialProjectId);
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<ReasonProblem | null>(null);
  const projectField = useRef<HTMLSelectElement>(null);
  const reasonField = useRef<HTMLTextAreaElement>(null);
  const projectFieldId = `${idPrefix}-project`;
  const reasonId = `${idPrefix}-reason`;
  const reasonHintId = `${idPrefix}-reason-hint`;
  const reasonErrorId = `${idPrefix}-reason-error`;
  const ordered =
    props.projectChoice === null
      ? []
      : orderProjects(props.projectChoice.projects, props.projectChoice.choices);

  useEffect(() => {
    (projectField.current ?? reasonField.current)?.focus();
  }, []);

  const form = (
    <form
      className={cx(styles["card"], styles["stack"])}
      aria-label={t("attributionRequest.title")}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const found = validateReason(reason);
        setProblem(found);

        if (found !== null || !online) {
          return;
        }

        mutateThen(
          create,
          { attribution: props.buildAttribution(projectId), reason: reason.trim() },
          { onSuccess: props.onDone },
        );
      }}
    >
      <h3>{t("attributionRequest.title")}</h3>
      <p>{props.subject}</p>
      <p className={styles["muted"]}>{t("attributionRequest.explanation")}</p>
      {props.projectChoice === null ? (
        <p>{t("attributionRequest.fixedProject", { project: props.fixedProjectLabel })}</p>
      ) : (
        <div className={styles["field"]}>
          <label htmlFor={projectFieldId}>{t("attributionRequest.project")}</label>
          <select
            id={projectFieldId}
            ref={projectField}
            value={projectId ?? ""}
            onChange={(event) => {
              setProjectId(event.target.value === "" ? null : event.target.value);
            }}
          >
            <option value="">{t("project.none")}</option>
            {ordered.map((project) => (
              <option key={project.id} value={project.id}>
                {project.code} · {project.name}
              </option>
            ))}
            {projectId !== null && !ordered.some((project) => project.id === projectId) ? (
              <option value={projectId}>{props.fixedProjectLabel}</option>
            ) : null}
          </select>
        </div>
      )}
      <div className={styles["field"]}>
        <label htmlFor={reasonId}>{t("attributionRequest.reason")}</label>
        <textarea
          id={reasonId}
          ref={reasonField}
          rows={3}
          value={reason}
          maxLength={maxRequestReasonLength}
          aria-required="true"
          aria-invalid={problem !== null}
          aria-describedby={problem === null ? reasonHintId : `${reasonHintId} ${reasonErrorId}`}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
        <span id={reasonHintId} className={styles["muted"]}>
          {t("attributionRequest.reasonHint", { max: String(maxRequestReasonLength) })}
        </span>
        {problem === null ? null : (
          <span id={reasonErrorId} role="alert">
            {t(`attributionRequest.reason.${problem}`)}
          </span>
        )}
      </div>
      {online ? null : <Alert tone="info">{t("attributionRequest.offline")}</Alert>}
      {create.isError ? <ErrorMessage error={create.error} /> : null}
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onCancel}>
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          className={cx(styles["button"], styles["primary"])}
          disabled={!online || create.isPending}
        >
          {t("attributionRequest.submit")}
        </button>
      </div>
    </form>
  );

  return form;
}
